import React, {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import isNil from 'lodash/isNil';
import { useTranslation } from 'react-i18next';
import {
    Checkbox, Table, Title,
} from 'ui-library';
import Preloader from 'components/Preloader';
import { Status, StatusBox } from 'components/StatusBox';
import { columnDefs, footerColumnDefs } from './columnDefs';
import { adaptAllocations } from './adaptAllocations';
import Icon from '../../../../../../ui-library/components/Icon/Icon';
import { ICON_TYPE_INFOBOX } from '../../../../../../ui-library/components/Icon';
import Popover from '../../../../../../ui-library/components/Popover';

interface KeyValue {
    [key: string]: string | number | boolean | Function | object | undefined;
}
interface Security {
    Id: number;
    Name: string;
}
interface Position {
    Allocation: number;
    Security: Security;
}
interface ModelPortfolioDetails {
    Positions?: Position[];
    // Including index signature if other dynamic properties exist
    [key: string]: any;
}
interface IndividualPortfolioData {
    body: KeyValue[]; // Replaces 'keyValueArray'
    footer: KeyValue; // Replaces 'keyValue'
}

// --- Component Props Interface ---

interface IndividualPortfolioSecuritiesProps {
    individualPortfolioData: IndividualPortfolioData;
    isLoading?: boolean;
    isRebalance?: boolean;
    setRebalance: (value: boolean) => void;
    modelPortfolioDetails: ModelPortfolioDetails;
    setIndividualPortfolioData: (data: any) => void;
    setValidationError: (error: string | null | undefined) => void;
    cachedSelectStrategy?: any;
    setupWithdrawalPlan?: boolean;
}

const IndividualPortfolioSecurities = ({
    isLoading = true,
    individualPortfolioData = { body: [], footer: {} },
    isRebalance = false,
    modelPortfolioDetails = {},
    setIndividualPortfolioData = () => {},
    setRebalance = () => {},
    setValidationError = () => {},
    cachedSelectStrategy,
    setupWithdrawalPlan,
}: IndividualPortfolioSecuritiesProps) => {
    const [changesAllocationSliderValue, setChangesAllocationSliderValue] = useState<{value: number, id?: number | undefined} | null>(null);
    const [changedPositionListValue, setChangedPositionListValue] = useState();
    const { t } = useTranslation();
    const isWithdrawalEnabled = useMemo(() => JSON.parse(modelPortfolioDetails?.Attributes || '{}')?.WithdrawalOption, [modelPortfolioDetails]);

    const onAllocationChange = useCallback((value: { value: number, id?: number | undefined }) => {
        setChangesAllocationSliderValue(value);
    }, []);

    const onCheckBoxChange = useCallback((value) => {
        setChangedPositionListValue(value);
    }, []);

    const onChangeRebalance = useCallback((e) => {
        setRebalance(e?.target?.checked);
    }, []);

    useEffect(() => () => {
        setValidationError(null);
    }, []);

    useEffect(() => {
        setValidationError(
            !isNil(individualPortfolioData?.footer?.allocation)
            && individualPortfolioData?.footer?.allocation !== 100
                ? t('portfolio.createNewPortfolio.strategy.individualPortfolio.error.OneHundred')
                : undefined,
        );
    }, [individualPortfolioData?.footer?.allocation]);

    const updateAllocationById = useCallback(({ id, allocationUpdateObject = {}, nameUpdateObject = {} }) => {
        const tableData = individualPortfolioData.body.map((item) => {
            if (item?.id === id) {
                return {
                    ...item,
                    allocation: {
                        ...item?.allocation,
                        ...allocationUpdateObject,
                    },
                    name: {
                        ...item?.name,
                        ...nameUpdateObject,
                    },
                };
            }

            return item;
        });

        const footerTableData = {
            ...individualPortfolioData.footer,
            allocation: tableData.reduce((accu, item) => ((item?.name.checked ? item?.allocation?.value : 0) + accu), 0),
        };

        setIndividualPortfolioData({
            body: tableData,
            footer: footerTableData,
        });
    }, [individualPortfolioData?.body, individualPortfolioData?.footer]);

    useEffect(() => {
        if (changesAllocationSliderValue) {
            const { value, id } = changesAllocationSliderValue;

            updateAllocationById({
                id,
                allocationUpdateObject: value === 0 ? { value, disabled: true } : { value },
                nameUpdateObject: value === 0 ? { checked: false } : {},
            });
        }
    }, [changesAllocationSliderValue]);

    useEffect(() => {
        if (changedPositionListValue) {
            const { checked, id } = changedPositionListValue;

            updateAllocationById({
                id,
                allocationUpdateObject: { value: checked ? 10 : 0, disabled: !checked },
                nameUpdateObject: { checked },
            });
        }
    }, [changedPositionListValue]);

    useEffect(() => {
        const data = adaptAllocations({
            t, data: modelPortfolioDetails, onAllocationChange, onCheckBoxChange,
        });
        const cachedIndividualPortfolio = cachedSelectStrategy?.individualPortfolio;

        if (cachedIndividualPortfolio?.length) {
            data.body = data.body.map((item) => {
                const cachedAllocation = cachedIndividualPortfolio.find((cachedItem) => (item?.allocation?.id === cachedItem.securityId));

                if (cachedAllocation) {
                    return {
                        ...item,
                        name: {
                            ...item?.name,
                            checked: true,
                        },
                        allocation: {
                            ...item?.allocation,
                            value: cachedAllocation.allocation * 100,
                            disabled: false,
                        },
                    };
                }

                return {
                    ...item,
                    name: {
                        ...item?.name,
                        checked: false,
                    },
                    allocation: {
                        ...item?.allocation,
                        disabled: true,
                    },
                };
            });

            data.footer = {
                ...data.footer,
                allocation: Math.round(100 * cachedIndividualPortfolio.reduce((accu, item) => (item.allocation + accu), 0)),
            };
        }

        if (typeof cachedSelectStrategy?.rebalancing === 'boolean') {
            setRebalance(cachedSelectStrategy?.rebalancing);
        }

        setIndividualPortfolioData(data);
    }, [
        t,
        modelPortfolioDetails,
        cachedSelectStrategy?.modelPortfolioId,
    ]);

    useEffect(() => {
        if (!isWithdrawalEnabled && setupWithdrawalPlan) {
            setIndividualPortfolioData({});
        }
    }, [isWithdrawalEnabled, setupWithdrawalPlan]);

    return (
        <div className="fi-product-mark-selection">
            {!isWithdrawalEnabled && setupWithdrawalPlan ? <StatusBox status={Status.Warning} description={t('portfolioDetail.strategy.withdrawalPlanNotSupportedForIndividual')} />
                : (
                    <>
                        <Title type={2}>{t('portfolio.createNewPortfolio.allocations')}</Title>
                        <Preloader isLoading={isLoading}>
                            <div className="new-portfolio-content">
                                <Table
                                    data={individualPortfolioData.body}
                                    columns={columnDefs(t)}
                                    footerColumns={footerColumnDefs(t)}
                                    mini={false}
                                    footer={individualPortfolioData.footer}
                                />

                                <div className="rebalance-container">
                                    <Checkbox
                                        className="rebalance"
                                        label={t('portfolio.createNewPortfolio.allocations.rebalance')}
                                        onChange={onChangeRebalance}
                                        checked={isRebalance}
                                    />
                                    <Popover content={<div style={{ padding: '10px', maxWidth: '300px' }}>{t('portfolioDetail.strategy.rebalancingTooltip')}</div>} trigger="hover">
                                        <Icon type={ICON_TYPE_INFOBOX} className="search-icon" size={16} />
                                    </Popover>
                                </div>
                            </div>
                        </Preloader>
                    </>
                )}
        </div>
    );
};

export default IndividualPortfolioSecurities;
